import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import type { Test } from "@/types";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Spinner } from "@/components/ui/Spinner";

const statusColor: Record<Test["status"], string> = {
  bozza: "#555577",
  in_revisione: "#5B3E8A",
  pubblicato: "#2A7A4B",
  chiuso: "#E8613C",
};

export default function TestList() {
  const [tests, setTests] = useState<Test[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  // Form nuovo test
  const [titolo, setTitolo] = useState("");
  const [slug, setSlug] = useState("");
  const [error, setError] = useState<string | null>(null);

  function load() {
    setLoading(true);
    api.get<Test[]>("/admin/tests")
      .then(setTests)
      .finally(() => setLoading(false));
  }
  useEffect(load, []);

  async function createTest(ev: React.FormEvent) {
    ev.preventDefault();
    setError(null);
    try {
      await api.post<Test>("/admin/tests", { titolo, slug, pubblicoAnonimo: false });
      setTitolo(""); setSlug(""); setCreating(false);
      load();
    } catch (e: any) {
      setError(e.message);
    }
  }

  async function publish(id: string) {
    try { await api.post(`/admin/tests/${id}/publish`); load(); }
    catch (e: any) { alert(e.message); }
  }
  async function close(id: string) {
    if (!confirm("Chiudere il test? Non accetterà più risposte.")) return;
    try { await api.post(`/admin/tests/${id}/close`); load(); }
    catch (e: any) { alert(e.message); }
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-5">
      <header className="flex justify-between items-center">
        <h1 className="text-3xl font-serif text-primary">Test</h1>
        <Button onClick={() => setCreating(!creating)} variant="secondary">
          {creating ? "Annulla" : "+ Nuovo test"}
        </Button>
      </header>

      {creating && (
        <Card>
          <form onSubmit={createTest} className="space-y-3">
            <Input label="Titolo" value={titolo} onChange={(e) => setTitolo(e.target.value)} required />
            <Input
              label="Slug (URL-friendly: solo a-z 0-9 -)"
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))}
              required
            />
            {error && <div className="text-red-300 text-sm">{error}</div>}
            <Button type="submit">Crea</Button>
          </form>
        </Card>
      )}

      {loading ? (
        <div className="flex justify-center py-10"><Spinner size={28} /></div>
      ) : tests.length === 0 ? (
        <Card><p className="text-secondary">Nessun test ancora. Creane uno per iniziare.</p></Card>
      ) : (
        <div className="space-y-2">
          {tests.map((t) => (
            <Card key={t.id}>
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge color={statusColor[t.status]}>{t.status}</Badge>
                    <span className="font-mono text-xs text-muted">/{t.slug}</span>
                  </div>
                  <Link to={`/admin/tests/${t.id}/edit`} className="text-primary font-serif text-lg hover:text-accent">
                    {t.titolo}
                  </Link>
                  <div className="text-xs text-muted font-mono mt-1">
                    {t._count?.questions ?? 0} domande · {t._count?.submissions ?? 0} submission
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Link to={`/admin/tests/${t.id}/domande`} className="bg-card border border-border text-primary px-3 py-2 rounded-xl font-mono text-xs hover:border-secondary">
                    Domande
                  </Link>
                  <Link to={`/admin/tests/${t.id}/risultati`} className="bg-card border border-border text-primary px-3 py-2 rounded-xl font-mono text-xs hover:border-secondary">
                    Risultati
                  </Link>
                  {t.status === "pubblicato" && (
                    <Link
                      to={`/admin/tests/${t.id}/invio`}
                      className="bg-purple text-white px-3 py-2 rounded-xl font-mono text-xs hover:brightness-110"
                    >
                      Invio credenziali
                    </Link>
                  )}
                  {t.status === "bozza" || t.status === "in_revisione" ? (
                    <button onClick={() => publish(t.id)} className="bg-green text-white px-3 py-2 rounded-xl font-mono text-xs hover:brightness-110">
                      Pubblica
                    </button>
                  ) : t.status === "pubblicato" ? (
                    <button onClick={() => close(t.id)} className="bg-accent text-white px-3 py-2 rounded-xl font-mono text-xs hover:brightness-110">
                      Chiudi
                    </button>
                  ) : null}
                </div>
              </div>

            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
